import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  selectIsAuthenticated, 
  selectUser, 
  logout,
  selectIsAuthModalOpen,
  selectAuthModalMode,
  openAuthModal,
  closeAuthModal,
  setAuthModalMode
} from '../../store/slices/authSlice';
import { IoArrowForward, IoPersonCircleOutline, IoChevronDown } from 'react-icons/io5';
import NavbarLogo from './../../assets/logos/navbarLogo.svg';
import AuthModal from '../Auth/AuthModal';
import LoginForm from '../Auth/LoginForm';
import SignupForm from '../Auth/SignupForm';
import ForgotPasswordForm from '../Auth/ForgotPasswordForm';
import './Navbar.css';

function Navbar() {
  const dispatch = useDispatch();
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Redux state
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const isAuthModalOpen = useSelector(selectIsAuthModalOpen);
  const authMode = useSelector(selectAuthModalMode);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 800;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const isHomePage = location.pathname === '/';
  const isBlogPage = location.pathname === '/blogs' || location.pathname.startsWith('/blogs/');
  const isContactPage = location.pathname === '/contactus';
  const isEventsPage = location.pathname === '/events';
  const isPolicyPage =
    location.pathname === '/privacy-policy' ||
    location.pathname === '/terms-conditions' ||
    location.pathname === '/fraud-alert' ||
    location.pathname === '/trust-safety' ||
    location.pathname === '/report-issue' ||
    location.pathname === '/help';

  const isTransparent = isHomePage && !scrolled && !mobileMenuOpen;
  
  const isCollegeFinderPage = location.pathname === '/college-finder';
  const isJobsPage = location.pathname.startsWith('/jobs');
  const isCounsellorPage = location.pathname === '/counsellor';
  const isCounsellorProfilePage = location.pathname.startsWith('/counsellor-profile/');

  const getNavbarClass = () => {
    if (mobileMenuOpen) return 'navbar-mobile-open';
    if (isHomePage && !scrolled) return 'navbar-home-transparent';
    if (isHomePage && scrolled) return 'navbar-home-scrolled';
    if (isBlogPage || isContactPage || isCollegeFinderPage || isJobsPage || isCounsellorPage || isCounsellorProfilePage || isEventsPage ||
        ['/login', '/signup', '/forgot-password', '/reset-password', '/profile', '/education-loan'].includes(location.pathname)) return 'navbar-blog';
    if (isPolicyPage) return 'navbar-policy';
    return 'navbar-default';
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    dispatch(openAuthModal('login'));
    setMobileMenuOpen(false);
  };

  const handleSwitchToSignup = () => dispatch(setAuthModalMode('signup'));
  const handleSwitchToLogin = () => dispatch(setAuthModalMode('login'));
  const handleSwitchToForgotPassword = () => dispatch(setAuthModalMode('forgot-password'));
  const handleCloseModal = () => dispatch(closeAuthModal());

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
  };

  const handleLogoClick = (e) => {
    handleMobileLinkClick();
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    setMobileMenuOpen(false);
    setProfileDropdownOpen(false);
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownOpen && !event.target.closest('.navbar-user-section')) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileDropdownOpen]);

  return (
    <>
      <nav className={`navbar ${getNavbarClass()}`} style={{zIndex: 999}}>
        <div className="navbar-container">
          <Link to="/" className="navbar-logo" onClick={handleLogoClick}>
            <img src={NavbarLogo} alt="Riso" className="logo-image" />
          </Link>

          {/* Login/User Section - Must be before menu for CSS positioning */}
          {isAuthenticated && user ? (
            <div className="navbar-user-section">
              <button className="navbar-profile-trigger" onClick={toggleProfileDropdown}>
                <IoPersonCircleOutline className="profile-icon" />
                <p className="profile-name1">{(user?.name || user?.firstName || 'User').split(' ')[0]}</p>
                <IoChevronDown className={`dropdown-arrow ${profileDropdownOpen ? 'open' : ''}`} />
              </button>
              
              {profileDropdownOpen && (
                <div className="profile-dropdown">
                  <Link to="/profile" className="dropdown-item" onClick={() => setProfileDropdownOpen(false)}>
                    <IoPersonCircleOutline className="dropdown-icon" />
                    <span>Profile</span>
                  </Link>
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <IoArrowForward className="dropdown-icon logout-icon" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              className="navbar-login-btn"
              onClick={handleLoginClick}
            >
              LOGIN
              <IoArrowForward className="login-arrow" />
            </button>
          )}

          {/* Desktop Menu */}
          <ul className="navbar-menu">
            <li>
              <Link
                to="/"
                className={location.pathname === '/' ? 'nav-link active' : 'nav-link'}
              >
                HOME
              </Link>
            </li>
            <li>
              <Link
                to="/college-finder"
                className={location.pathname === '/college-finder' ? 'nav-link active' : 'nav-link'}
              >
                COLLEGES
              </Link>
            </li>
            <li>
              <Link
                to="/blogs"
                className={
                  location.pathname === '/blogs' || location.pathname.startsWith('/blogs/')
                    ? 'nav-link active'
                    : 'nav-link'
                }
              >
                BLOGS
              </Link>
            </li>
            
            <li>
              <Link
                to="/events"
                className={location.pathname === '/events' ? 'nav-link active' : 'nav-link'}
              >
                EVENTS
              </Link>
            </li>
            <li>
              <Link
                to="/contactus"
                className={location.pathname === '/contactus' ? 'nav-link active' : 'nav-link'}
              >
                CONTACT
              </Link>
            </li>
          </ul>

          {/* Mobile Menu Toggle */}
          <div className="navbar-mobile-toggle" onClick={handleMobileMenuToggle}>
            <div className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div className={`navbar-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <ul className="mobile-menu-links">
            <li>
              <Link
                to="/"
                className={location.pathname === '/' ? 'mobile-nav-link active' : 'mobile-nav-link'}
                onClick={handleMobileLinkClick}
              >
                HOME
              </Link>
            </li>
            <li>
              <Link
                to="/about"
                className={location.pathname === '/about' ? 'mobile-nav-link active' : 'mobile-nav-link'}
                onClick={handleMobileLinkClick}
              >
                ABOUT US
              </Link>
            </li>
            <li>
              <Link
                to="/college-finder"
                className={location.pathname === '/college-finder' ? 'mobile-nav-link active' : 'mobile-nav-link'}
                onClick={handleMobileLinkClick}
              >
                COLLEGES
              </Link>
            </li>
            <li>
              <Link
                to="/blogs"
                className={
                  location.pathname === '/blogs' || location.pathname.startsWith('/blogs/')
                    ? 'mobile-nav-link active'
                    : 'mobile-nav-link'
                }
                onClick={handleMobileLinkClick}
              >
                BLOGS
              </Link>
            </li>
           
            <li>
              <Link
                to="/events"
                className={location.pathname === '/events' ? 'mobile-nav-link active' : 'mobile-nav-link'}
                onClick={handleMobileLinkClick}
              >
                EVENTS
              </Link>
            </li>
            <li>
              <Link
                to="/contactus"
                className={location.pathname === '/contactus' ? 'mobile-nav-link active' : 'mobile-nav-link'}
                onClick={handleMobileLinkClick}
              >
                CONTACT
              </Link>
            </li>
            <li>
              {isAuthenticated && user ? (
                <div className="mobile-user-section">
                  <span className="mobile-user-name">Hi, {user.name}</span>
                  <Link 
                    to="/profile" 
                    className="mobile-nav-link" 
                    onClick={handleMobileLinkClick}
                    style={{ fontSize: '20px' }}
                  >
                    MY PROFILE
                  </Link>
                  <button className="mobile-logout-btn" onClick={handleLogout}>
                    LOGOUT
                  </button>
                </div>
              ) : (
                <button className="mobile-login-btn" onClick={handleLoginClick}>
                  LOGIN
                  <IoArrowForward className="login-arrow" />
                </button>
              )}
            </li>
          </ul>
        </div>
      </nav>

      <AuthModal isOpen={isAuthModalOpen} onClose={handleCloseModal}>
        {authMode === 'login' && (
          <LoginForm 
            onSwitchToSignup={handleSwitchToSignup}
            onSwitchToForgotPassword={handleSwitchToForgotPassword}
            onClose={handleCloseModal}
          />
        )}
        {authMode === 'signup' && (
          <SignupForm 
            onSwitchToLogin={handleSwitchToLogin}
            onClose={handleCloseModal}
          />
        )}
        {authMode === 'forgot-password' && (
          <ForgotPasswordForm 
            onSwitchToLogin={handleSwitchToLogin}
            onSwitchToSignup={handleSwitchToSignup}
            onClose={handleCloseModal}
          />
        )}
      </AuthModal>
    </>
  );
}

export default Navbar;
